import {
  Badge,
  Button,
  CardGrid,
  EmptyState,
  Price,
  SearchBar,
  SellerBadge,
} from '@beautyburst/ui';
import { AVAILABILITY_LABEL_TEXT } from '@beautyburst/types';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import { customerHref } from '../base-path.ts';
import type { AvailabilityResult, StoreCategoryRow, StorePolicyRow } from '@beautyburst/customer';
import type { FormEvent } from 'react';
import type { CustomerApiClient } from '../api-client.ts';
import { CartAddFeedback, useCartAddFeedback } from '../cart-add-feedback.tsx';

export interface StorefrontProduct {
  listingId: string;
  productId: string;
  productName: string;
  productSlug: string;
  variantId: string;
  variantName: string;
  priceCents: number;
  salePriceCents: number | null;
  saleActive: boolean;
  effectivePriceCents: number;
  sellerDisplayName: string;
  ownershipType: 'RETAILER' | 'BEAUTY_BURST_DIRECT';
  available: number | null;
  mediaKey: string | null;
  mediaAltText: string | null;
}
export interface StorefrontData {
  slug: string;
  name: string;
  sellerDisplayName: string;
  ownershipType: 'RETAILER' | 'BEAUTY_BURST_DIRECT';
  tagline: string | null;
  logoMediaKey: string | null;
  heroMediaKey: string | null;
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
  publicHoursSummary: string;
  availability?: AvailabilityResult | null;
  distanceMiles?: number | null;
  averageRating?: number | null;
  reviewCount: number;
  categories: readonly StoreCategoryRow[];
  promotions: readonly unknown[];
  policies: readonly StorePolicyRow[];
  products: readonly StorefrontProduct[];
}
export function StorefrontScreen({
  store,
  mediaUrlFor,
  onSearch,
  client,
}: {
  store: StorefrontData;
  mediaUrlFor?: (mediaKey: string) => string | null;
  onSearch?: (path: string) => void;
  client?: Pick<CustomerApiClient, 'cart'>;
}) {
  const banner =
    store.heroMediaKey === null || mediaUrlFor === undefined
      ? null
      : mediaUrlFor(store.heroMediaKey);
  return (
    <section aria-labelledby="store-heading" style={{ display: 'grid', gap: space[5] }}>
      {banner === null ? null : <img src={banner} alt={`${store.name} storefront`} />}
      <header>
        <h1
          id="store-heading"
          style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary, margin: 0 }}
        >
          {store.name}
        </h1>
        {store.tagline === null ? null : <p>{store.tagline}</p>}
        <SellerBadge
          retailerName={
            store.ownershipType === 'BEAUTY_BURST_DIRECT' ? null : store.sellerDisplayName
          }
        />
        <p>
          {typeof store.averageRating !== 'number'
            ? 'No reviews yet'
            : `${store.averageRating.toFixed(1)} out of 5 from ${store.reviewCount} reviews`}
        </p>
        <p>{store.publicHoursSummary}</p>
        {store.availability === null || store.availability === undefined ? null : (
          <Badge variant="info">{AVAILABILITY_LABEL_TEXT[store.availability.label]}</Badge>
        )}
        {typeof store.distanceMiles === 'number' ? (
          <p>{store.distanceMiles.toFixed(1)} mi away</p>
        ) : (
          <p>Distance unavailable</p>
        )}
        <div>
          {store.pickupEnabled ? <Badge variant="info">Pickup available</Badge> : null}
          {store.deliveryEnabled ? (
            <Badge variant="info">Delivery availability is confirmed at bag review</Badge>
          ) : null}
        </div>
      </header>
      <section aria-labelledby="store-categories">
        <h2 id="store-categories" style={typeStyle('headingSm')}>
          Shop this store
        </h2>
        <ul>
          {store.categories.map((category) => (
            <li key={category.id}>
              <a href={customerHref(`/categories/${category.slug}`)}>
                {category.name} ({category.count})
              </a>
            </li>
          ))}
        </ul>
      </section>
      {store.promotions.length === 0 ? null : (
        <section aria-label="Store promotions">Promotions are available.</section>
      )}
      <section aria-labelledby="store-products">
        <h2 id="store-products" style={typeStyle('headingSm')}>
          Products
        </h2>
        <SearchBar
          action={customerHref('/search')}
          label={`Search products at ${store.name}`}
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const query = new FormData(event.currentTarget).get('q');
            const params = new URLSearchParams();
            if (typeof query === 'string' && query.trim() !== '') params.set('q', query.trim());
            onSearch?.(customerHref(`/search${params.size === 0 ? '' : `?${params.toString()}`}`));
          }}
        />
        {store.products.length === 0 ? (
          <EmptyState
            title="No products match this store search"
            description="Try another product name or browse this store’s categories."
          />
        ) : (
          <CardGrid label={`${store.name} products`}>
            <>
              {store.products.map((product) => (
                <StorefrontProductRow key={product.listingId} product={product} client={client} />
              ))}
            </>
          </CardGrid>
        )}
      </section>
      <section aria-labelledby="store-policies">
        <h2 id="store-policies" style={typeStyle('headingSm')}>
          Store policies
        </h2>
        {store.policies.length === 0 ? (
          <p>Store policies have not been published.</p>
        ) : (
          <dl>
            {store.policies.map((policy) => (
              <div key={policy.policy_kind}>
                <dt>{policy.title}</dt>
                <dd>{policy.body}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>
    </section>
  );
}

function StorefrontProductRow({
  product,
  client,
}: {
  product: StorefrontProduct;
  client?: Pick<CustomerApiClient, 'cart'>;
}) {
  const { addToBag, dismiss, feedback } = useCartAddFeedback(client);
  const canAddToBag = product.available !== null && product.available > 0;
  return (
    <li>
      <a href={customerHref(`/products/${product.productSlug}`)}>{product.productName}</a>
      <p>{product.variantName}</p>
      <Price
        priceCents={product.priceCents}
        saleActive={product.saleActive}
        salePriceCents={product.salePriceCents}
      />
      <SellerBadge
        retailerName={
          product.ownershipType === 'BEAUTY_BURST_DIRECT' ? null : product.sellerDisplayName
        }
      />
      <p>
        {product.available === null
          ? 'Stock unavailable'
          : product.available > 0
            ? 'In stock'
            : 'Out of stock'}
      </p>
      <Button
        variant="primary"
        size="sm"
        disabled={!canAddToBag}
        onClick={() => void addToBag({ listingId: product.listingId, quantity: 1 })}
      >
        Add to Bag
      </Button>
      <CartAddFeedback feedback={feedback} onDismiss={dismiss} />
    </li>
  );
}
